import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useState } from 'react';

import type { ListKey } from '@feathr/hooks';
import { useList } from '@feathr/hooks';

export interface IUseTableSelectionOptions {
  /**
   * Initial list of selected items
   */
  initialItems?: Array<string | number>;

  /**
   * Function to get all items for "select all" functionality
   * Should return array of IDs for all items (not just current page)
   */
  getAllItems?: () => Promise<Array<string | number>> | Array<string | number>;
}

export interface IUseTableSelectionReturn {
  /**
   * Selected items (for Table component's selected prop)
   */
  selected: string[];

  /**
   * Setter for selected items (for Table component's setSelected prop)
   */
  setSelected: Dispatch<SetStateAction<string[]>>;

  /**
   * Select all items on current page
   */
  selectPage: (pageItems: Array<string | number>) => void;

  /**
   * Switch to "all mode" - selects all items conceptually without loading all IDs
   */
  selectAll: () => void;

  /**
   * Clear all selections and exit all mode
   */
  clearSelection: () => void;

  /**
   * Whether any items are selected (including all mode)
   */
  hasSelection: boolean;

  /**
   * Number of selected items (returns totalCount when in all mode, otherwise actual count)
   */
  count: number;

  /**
   * Whether currently in "all mode" (all items selected)
   */
  isSelectAllMode: boolean;

  /**
   * Total count of items (used for all mode display)
   */
  totalCount?: number;

  /**
   * Set total count (used for all mode calculations)
   */
  setTotalCount: (total: number) => void;

  /**
   * Get selected item IDs (returns empty array in all mode - use isSelectAllMode instead)
   */
  getSelected: () => Array<string | number>;

  /**
   * Check if specific item is selected
   */
  isSelected: (item: string | number) => boolean;

  /**
   * Auto-select all items on the current page (used by Table component in all mode)
   */
  autoSelectPage: (pageItems: Array<string | number>) => void;
}

/**
 * Hook for managing table selection state using useList internally.
 * Provides table-specific selection methods and integrates with Table component props.
 *
 * @param key - List key from LIST_KEYS (e.g., SELECTED_USERS, SELECTED_PERSONS)
 * @param options - Configuration options
 * @returns Object with selection state and methods for table integration
 *
 * @example
 * ```typescript
 * const selection = useTableSelection(SELECTED_PERSONS, {
 *   getAllItems: () => Persons.getAllIds()
 * });
 *
 * return (
 *   <Table
 *     isSelectable={true}
 *     selected={selection.selected}
 *     setSelected={selection.setSelected}
 *   />
 * );
 * ```
 */
export function useTableSelection(
  key: ListKey,
  options: IUseTableSelectionOptions = {},
): IUseTableSelectionReturn {
  const { initialItems } = options;
  const list = useList(key, { initialItems });

  // State for "all mode" and total count
  const [isSelectAllMode, setIsSelectAllMode] = useState(false);
  const [totalCount, setTotalCount] = useState<number | undefined>();

  // Extract stable references to avoid dependency issues
  const { get, set, add, reset, has, isEmpty, count } = list;

  const setSelected = useCallback<Dispatch<SetStateAction<string[]>>>(
    (value) => {
      // Exit all mode when manually selecting items
      setIsSelectAllMode(false);

      if (typeof value === 'function') {
        const currentSelected = get().map(String);
        const newSelected = value(currentSelected);
        set(newSelected);
      } else {
        set(value);
      }
    },
    [get, set],
  );

  const selectPage = useCallback(
    (pageItems: Array<string | number>) => {
      // Exit all mode when selecting page items
      setIsSelectAllMode(false);
      add(pageItems);
    },
    [add],
  );

  const selectAll = useCallback(() => {
    // Switch to all mode instead of loading all items
    setIsSelectAllMode(true);
    // Clear individual selections when entering all mode
    reset();
  }, [reset]);

  const clearSelection = useCallback(() => {
    setIsSelectAllMode(false);
    reset();
  }, [reset]);

  const autoSelectPage = useCallback(
    (pageItems: Array<string | number>) => {
      // Only auto-select if we're in all mode
      if (isSelectAllMode) {
        set(pageItems);
      }
    },
    [isSelectAllMode, set],
  );

  return {
    selected: get().map(String),
    setSelected,
    selectPage,
    selectAll,
    clearSelection,
    hasSelection: isSelectAllMode || !isEmpty,
    count: isSelectAllMode ? totalCount || 0 : count,
    isSelectAllMode,
    totalCount,
    setTotalCount,
    getSelected: get,
    isSelected: has,
    autoSelectPage,
  };
}
